import React, { useState, useEffect } from 'react';
import { Download, Bell, X } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const PWAPrompt = () => {
  const {
    isInstallable,
    isInstalled,
    notificationPermission,
    installApp,
    requestNotificationPermission,
    subscribeToPush
  } = usePWA();

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Afficher le prompt d'installation si possible
    if (isInstallable && !isInstalled) {
      const installDismissed = localStorage.getItem('pwa-install-dismissed');
      if (!installDismissed) {
        setShowInstallPrompt(true);
      }
    }

    // Afficher le prompt de notifications si installé mais pas de permission
    if (isInstalled && notificationPermission === 'default') {
      const notifDismissed = localStorage.getItem('pwa-notif-dismissed');
      if (!notifDismissed) {
        setShowNotificationPrompt(true);
      }
    }
  }, [isInstallable, isInstalled, notificationPermission]);

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      setShowInstallPrompt(false);
    }
  };

  const handleRequestNotification = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      // S'abonner automatiquement aux push notifications
      await subscribeToPush();
      setShowNotificationPrompt(false);
    } else if (notificationPermission !== 'default') {
      setShowNotificationPrompt(false);
    }
  };

  const handleDismiss = (type) => {
    setDismissed(true);
    
    if (type === 'install') {
      localStorage.setItem('pwa-install-dismissed', 'true');
      setShowInstallPrompt(false);
    } else if (type === 'notification') {
      localStorage.setItem('pwa-notif-dismissed', 'true');
      setShowNotificationPrompt(false);
    }
  };

  if (dismissed || (!showInstallPrompt && !showNotificationPrompt)) {
    return null;
  }

  return (
    <>
      {/* Prompt d'installation */}
      {showInstallPrompt && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-primary text-white rounded-lg shadow-2xl p-4 z-50 animate-slide-up">
          <button
            onClick={() => handleDismiss('install')}
            className="absolute top-2 right-2 text-white hover:text-gray-200"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="bg-white text-primary p-2 rounded-lg flex-shrink-0">
              <Download size={24} />
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">
                Installer SuiviPlus
              </h3>
              <p className="text-sm text-white/70 mb-3">
                Accédez rapidement à l'application depuis votre écran d'accueil et recevez des alertes de stock.
              </p>
              
              <div className="flex gap-2">
                <button
                  onClick={handleInstall}
                  className="bg-white text-primary px-4 py-2 rounded-lg font-medium text-sm hover:bg-primary/5 transition"
                >
                  Installer
                </button>
                <button
                  onClick={() => handleDismiss('install')}
                  className="bg-primary-dark text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-primary-darker transition"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt de notifications */}
      {showNotificationPrompt && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-accent text-white rounded-lg shadow-2xl p-4 z-50 animate-slide-up">
          <button
            onClick={() => handleDismiss('notification')}
            className="absolute top-2 right-2 text-white hover:text-gray-200"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="bg-white text-accent p-2 rounded-lg flex-shrink-0">
              <Bell size={24} />
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">
                Activer les notifications
              </h3>
              <p className="text-sm text-white/70 mb-3">
                Recevez des alertes en temps réel pour les ruptures de stock et les stocks bas, même quand l'app est fermée.
              </p>
              
              <div className="flex gap-2">
                <button
                  onClick={handleRequestNotification}
                  className="bg-white text-accent px-4 py-2 rounded-lg font-medium text-sm hover:bg-accent/5 transition"
                >
                  Activer
                </button>
                <button
                  onClick={() => handleDismiss('notification')}
                  className="bg-accent-dark text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-accent-dark transition"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAPrompt;
